// Footer.js
import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaWhatsapp, 
  FaFacebook, 
  FaInstagram, 
  FaYoutube, 
  FaShippingFast, 
  FaFileContract, 
  FaShieldAlt,
  FaChevronRight,
  FaRocket,
  FaEye
} from 'react-icons/fa';
import './footer.css';
import logo from '../Assets/logo.png';

function Footer() {
  const [contactData, setContactData] = useState(null);
  const [visitorCount, setVisitorCount] = useState(null);
  const [socialData, setSocialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_BASE_URL}content.php`);
        const { body } = response.data;
        setContactData(body.contact);
        setVisitorCount(body.visitor_count);
        // Assuming social links come from the same API or a separate one
        // You might need to adjust this based on your actual API response
        setSocialData({
          facebook_url: body.contact.facebook_url || '#',
          instagram_url: body.contact.instagram_url || '#',
          youtube_url: body.contact.youtube_url || '#'
        });
      } catch (error) {
        console.error("Error fetching the contact data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactData();
  }, []);

  if (isLoading) {
    return (
      <div className="footer-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!contactData) {
    return (
      <div className="footer-loading">
        <p>Unable to load footer content</p>
      </div>
    );
  }

  return (
    <footer className="premium-footer">
      {/* Animated Gradient Border */}
      <div className="footer-gradient-border"></div>
      
      <div className="footer-container">
        {/* Main Footer Grid */}
        <div className="footer-grid">
          
          {/* Column 1 - Brand, Location & Visitor Counter */}
          <div className="footer-col brand-col">
            <div className="footer-brand">
              <h3 className="brand-name">
                {/* <span>BK Crackers</span> */}
              </h3>
              <img src={logo} alt="My Crackers" className="footer-logo" />
            </div>
            
            {/* Main Address */}
            <div className="location-card">
              <div className="location-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="location-text">
                <strong>Main Address</strong>
                <p>{contactData.address}</p>
              </div>
            </div>

            {/* Branch Address */}
            {contactData.branch_address && (
              <div className="location-card branch-address">
                <div className="location-icon branch-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="location-text">
                  <strong>Branch Address</strong>
                  <p>{contactData.branch_address}</p>
                </div>
              </div>
            )}

            {/* Visitor Counter */}
            <div className="visitor-counter-card">
              <div className="visitor-icon">
                <FaEye />
              </div>
              <div className="visitor-text">
                <strong>Website Visitors</strong>
                <p className="visitor-count">
                  {visitorCount?.visitor_count?.toLocaleString() || '0'}
                </p>
              </div>
            </div>

            <div className="footer-badges">
              <span className="badge certified">
                <FaShieldAlt /> ISO Certified
              </span>
            </div>
          </div>

          {/* Column 2 - Quick Links with Social Icons */}
          <div className="footer-col links-col">
            <div className="col-header">
              <div className="header-accent"></div>
              <h4>Quick Links</h4>
            </div>
            <div className="links-list">
              <Link to="/shippingpolicy" className="footer-link">
                <FaShippingFast />
                <span>Shipping & Delivery</span>
                <FaChevronRight className="link-arrow" />
              </Link>
              <Link to="/terms" className="footer-link">
                <FaFileContract />
                <span>Terms & Conditions</span>
                <FaChevronRight className="link-arrow" />
              </Link>
              <Link to="/privacy" className="footer-link">
                <FaShieldAlt />
                <span>Privacy Policy</span>
                <FaChevronRight className="link-arrow" />
              </Link>
              <Link to="/about" className="footer-link">
                <FaRocket />
                <span>About Us</span>
                <FaChevronRight className="link-arrow" />
              </Link>
            </div>

            {/* Social Icons - Moved Under Quick Links */}
            <div className="footer-social-section">
              <h4 className="social-section-title">Connect With Us</h4>
              <div className="footer-social-icons">
                <a 
                  href={contactData.facebook_url || socialData?.facebook_url || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="footer-social-icon fb"
                >
                  <FaFacebook />
                  <span className="social-tooltip">Facebook</span>
                </a>
                <a 
                  href={contactData.instagram_url || socialData?.instagram_url || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="footer-social-icon ig"
                >
                  <FaInstagram />
                  <span className="social-tooltip">Instagram</span>
                </a>
                <a 
                  href={contactData.youtube_url || socialData?.youtube_url || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="footer-social-icon yt"
                >
                  <FaYoutube />
                  <span className="social-tooltip">YouTube</span>
                </a>
              </div>
            </div>
          </div>

          {/* Column 3 - Contact Info with Multi-color Icons */}
          <div className="footer-col contact-col">
            <div className="col-header">
              <div className="header-accent"></div>
              <h4>Contact Us</h4>
            </div>
            <div className="contact-list">
              <a href={`tel:${contactData.phonenumber}`} className="contact-card phone">
                <div className="contact-icon phone-icon">
                  <FaPhone />
                </div>
                <div className="contact-details">
                  <span className="contact-label">Call Us</span>
                  <span className="contact-value">{contactData.phonenumber}</span>
                </div>
              </a>
              <a href={`mailto:${contactData.email}`} className="contact-card email">
                <div className="contact-icon email-icon">
                  <FaEnvelope />
                </div>
                <div className="contact-details">
                  <span className="contact-label">Email Us</span>
                  <span className="contact-value">{contactData.email}</span>
                </div>
              </a>
              <a href={`https://wa.me/${contactData.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="contact-card whatsapp">
                <div className="contact-icon whatsapp-icon">
                  <FaWhatsapp />
                </div>
                <div className="contact-details">
                  <span className="contact-label">WhatsApp</span>
                  <span className="contact-value">{contactData.whatsapp_number}</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Enhanced Attractive Warning Message */}
        <div className="warning-message">
          <div className="warning-inner">
            <div className="warning-icon">
              <span>⚠️</span>
              <div className="warning-pulse"></div>
            </div>
            <div className="warning-content">
              <div className="warning-title">Important Legal Notice</div>
              <div className="warning-text">
                As per Supreme Court guidelines (2018), online sale of firecrackers is strictly prohibited. 
                We fully comply with this order. Products displayed are for <strong>reference and catalog purposes only</strong>. 
                To place an order, please add items to <strong>Quick Purchase</strong> and submit an enquiry. 
                Our team will contact you within <strong>24 hours</strong> via WhatsApp or Phone.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="copyright-bar">
        <div className="footer-container">
          <div className="copyright-content">
            <p>Copyright © {new Date().getFullYear()} <strong className="copyright-brand">BK Crackers</strong>. All Rights Reserved.</p>
            <p className="developer-credit">
              Developed by <a href="https://aatheshsoft.com/" target="_blank" rel="noopener noreferrer">Aathesh Soft Solutions</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;